import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import { fetchLecturers, Lecturer } from '../services/lecturerApi';
import { fetchLectures, Lecture } from '../services/lectureApi';
import { fetchLectureCategories, LectureCategory } from '../services/lectureCategoryApi';
import { getCategoryImageWithFallback, getLecturerImageWithFallback } from '../utils/imageUtils';

// 데이터 검증 헬퍼 함수들
const validateLecturer = (lecturer: any): Lecturer => {
  return {
    ...lecturer,
    name: String(lecturer?.name || ''),
    introduction: String(lecturer?.introduction || ''),
    image_url: lecturer?.image_url ? String(lecturer.image_url) : null
  };
};

const validateLecture = (lecture: any): Lecture => {
  // 실제 데이터가 lecture_info 안에 있는 경우 처리
  const lectureInfo = lecture?.lecture_info || lecture;
  const lecturerInfo = lecture?.lecturer_info;
  
  // 디버깅을 위한 콘솔 로그
  // console.log('Raw lecture data:', lecture);
  // console.log('lectureInfo:', lectureInfo);
  // console.log('lecturerInfo:', lecturerInfo);
  // console.log('Original caption:', lectureInfo?.caption);
  
  // caption이 없거나 null인 경우 lecturer 정보로 대체
  let caption = String(lectureInfo?.caption || '');
  if (!caption || caption === 'null' || caption === '') {
    if (lecturerInfo) {
      caption = `${String(lecturerInfo.name || '')} · ${String(lecturerInfo.introduction || '')}`;
      console.log('Generated caption from lecturer info:', caption);
    } else {
      caption = '강의 소개';
      console.log('Using fallback caption:', caption);
    }
  }
  
  const validatedLecture = {
    id: lectureInfo?.id || lecture?.id || 0,
    title: String(lectureInfo?.title || ''),
    caption: caption,
    thumbnail_url: lectureInfo?.thumbnail_url || null,
    lecturer_info: lecturerInfo ? validateLecturer(lecturerInfo) : null
  };
  
  console.log('Final validated lecture:', validatedLecture);
  return validatedLecture;
};

const validateCategory = (category: any): LectureCategory => {
  return {
    ...category,
    name: String(category?.name || ''),
    image_url: (category?.optimized_image_url || category?.image_url) ? String(category.optimized_image_url || category.image_url) : null,
    route_name: String(category?.route_name || '')
  };
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [classroomPage, setClassroomPage] = useState(0); // API는 0부터 시작으로 변경
  const [connectPage, setConnectPage] = useState(0); // API는 0부터 시작
  const [allLecturers, setAllLecturers] = useState<Lecturer[][]>([]); // 모든 페이지의 강연자 데이터
  const [allLectures, setAllLectures] = useState<Lecture[][]>([]); // 모든 페이지의 강의 데이터
  const [categories, setCategories] = useState<LectureCategory[]>([]); // 강의 카테고리 데이터
  const [lecturerTotalPages, setLecturerTotalPages] = useState(1);
  const [lectureTotalPages, setLectureTotalPages] = useState(1);
  const [lectureLoading, setLectureLoading] = useState(false);
  const [lecturerLoading, setLecturerLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [imageLoadStates, setImageLoadStates] = useState<{ [key: number]: boolean }>({});
  
  // 드래그 상태 관리 - connect 섹션 추가
  const [classroomDrag, setClassroomDrag] = useState({
    isDragging: false,
    startX: 0,
    currentX: 0,
    offset: 0,
    hasMoved: false,
    preventClick: false
  });
  
  const [connectDrag, setConnectDrag] = useState({
    isDragging: false,
    startX: 0,
    currentX: 0,
    offset: 0,
    hasMoved: false,
    preventClick: false
  });
  
  // 컨테이너 참조
  const classroomRef = useRef<HTMLDivElement>(null);
  const connectRef = useRef<HTMLDivElement>(null);

  // 강의 카테고리 데이터 가져오기
  const loadCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await fetchLectureCategories();
      if (response.code === 200 && Array.isArray(response.data)) {
        const validatedCategories = response.data.map(validateCategory);
        setCategories(validatedCategories);
        console.log('강의 카테고리 데이터 로드 완료:', validatedCategories);
      }
    } catch (error) {
      console.error('강의 카테고리 데이터 로딩 실패:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // 모든 강연자 데이터 가져오기 (최적화: 필요할 때만)
  const loadAllLecturers = async () => {
    setLecturerLoading(true);
    setImageLoadStates({});
    try {
      // 첫 번째 페이지로 총 페이지 수 확인
      const firstResponse = await fetchLecturers({ page: 0, size: 4 });
      if (firstResponse.code === 200) {
        const totalPages = firstResponse.data.totalPages;
        setLecturerTotalPages(totalPages);
        
        // 첫 3페이지만 미리 로드 (나머지는 필요할 때 로드)
        const allPages: Lecturer[][] = [];
        const pagesToPreload = Math.min(3, totalPages);
        
        for (let page = 0; page < pagesToPreload; page++) {
          const response = await fetchLecturers({ page, size: 4 });
          if (response.code === 200 && Array.isArray(response.data.content)) {
            allPages[page] = response.data.content.map(validateLecturer);
          }
        }
        
        setAllLecturers(allPages);
        console.log(`처음 ${pagesToPreload}개 강연자 페이지 로드 완료:`, allPages);
      }
    } catch (error) {
      console.error('강연자 데이터 로딩 실패:', error);
    } finally {
      setLecturerLoading(false);
    }
  };

  // 모든 강의 데이터 가져오기 (최적화: 필요할 때만)
  const loadAllLectures = async () => {
    setLectureLoading(true);
    try {
      // 첫 번째 페이지로 총 페이지 수 확인
      const firstResponse = await fetchLectures({ page: 0, size: 3 });
      if (firstResponse.code === 200) {
        const totalPages = firstResponse.data.totalPages;
        setLectureTotalPages(totalPages);
        
        // 첫 3페이지만 미리 로드 (나머지는 필요할 때 로드)
        const allPages: Lecture[][] = [];
        const pagesToPreload = Math.min(3, totalPages);
        
        for (let page = 0; page < pagesToPreload; page++) {
          const response = await fetchLectures({ page, size: 3 });
          if (response.code === 200 && Array.isArray(response.data.content)) {
            allPages[page] = response.data.content.map(validateLecture);
          }
        }
        
        setAllLectures(allPages);
        console.log(`처음 ${pagesToPreload}개 강의 페이지 로드 완료:`, allPages);
      }
    } catch (error) {
      console.error('강의 데이터 로딩 실패:', error);
    } finally {
      setLectureLoading(false);
    }
  };

  // 이미지 로딩 완료 핸들러
  const handleImageLoad = (lecturerId: number) => {
    setImageLoadStates(prev => ({
      ...prev,
      [lecturerId]: true
    }));
  };

  // 특정 페이지 지연 로딩
  const loadLecturerPageIfNeeded = async (page: number) => {
    if (allLecturers[page] || page >= lecturerTotalPages) return;
    
    try {
      const response = await fetchLecturers({ page, size: 4 });
      if (response.code === 200 && Array.isArray(response.data.content)) {
        const newLecturers = [...allLecturers];
        newLecturers[page] = response.data.content.map(validateLecturer);
        setAllLecturers(newLecturers);
        console.log(`강연자 페이지 ${page} 지연 로드 완료`);
      }
    } catch (error) {
      console.error(`강연자 페이지 ${page} 지연 로딩 실패:`, error);
    }
  };

  const loadLecturePageIfNeeded = async (page: number) => {
    if (allLectures[page] || page >= lectureTotalPages) return;
    
    try {
      const response = await fetchLectures({ page, size: 3 });
      if (response.code === 200 && Array.isArray(response.data.content)) {
        const newLectures = [...allLectures];
        newLectures[page] = response.data.content.map(validateLecture);
        setAllLectures(newLectures);
        console.log(`강의 페이지 ${page} 지연 로드 완료`);
      }
    } catch (error) {
      console.error(`강의 페이지 ${page} 지연 로딩 실패:`, error);
    }
  };

  const handleClassroomPageChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && classroomPage > 0) {
      const newPage = classroomPage - 1;
      setClassroomPage(newPage);
      // 필요한 페이지 지연 로딩
      loadLecturePageIfNeeded(newPage);
      if (newPage > 0) loadLecturePageIfNeeded(newPage - 1);
      if (newPage < lectureTotalPages - 1) loadLecturePageIfNeeded(newPage + 1);
    } else if (direction === 'next' && classroomPage < lectureTotalPages - 1) {
      const newPage = classroomPage + 1;
      setClassroomPage(newPage);
      // 필요한 페이지 지연 로딩
      loadLecturePageIfNeeded(newPage);
      if (newPage > 0) loadLecturePageIfNeeded(newPage - 1);
      if (newPage < lectureTotalPages - 1) loadLecturePageIfNeeded(newPage + 1);
    }
  };

  const handleConnectPageChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && connectPage > 0) {
      const newPage = connectPage - 1;
      setConnectPage(newPage);
      // 필요한 페이지 지연 로딩
      loadLecturerPageIfNeeded(newPage);
      if (newPage > 0) loadLecturerPageIfNeeded(newPage - 1);
      if (newPage < lecturerTotalPages - 1) loadLecturerPageIfNeeded(newPage + 1);
    } else if (direction === 'next' && connectPage < lecturerTotalPages - 1) {
      const newPage = connectPage + 1;
      setConnectPage(newPage);
      // 필요한 페이지 지연 로딩
      loadLecturerPageIfNeeded(newPage);
      if (newPage > 0) loadLecturerPageIfNeeded(newPage - 1);
      if (newPage < lecturerTotalPages - 1) loadLecturerPageIfNeeded(newPage + 1);
    }
  };

  // 드래그 핸들러들 - 각 섹션별로 분리
  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent, section: 'classroom' | 'connect') => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    
    if (section === 'classroom') {
      setClassroomDrag({
        isDragging: true,
        startX: clientX,
        currentX: clientX,
        offset: 0,
        hasMoved: false,
        preventClick: false
      });
    } else {
      setConnectDrag({
        isDragging: true,
        startX: clientX,
        currentX: clientX,
        offset: 0,
        hasMoved: false,
        preventClick: false
      });
    }
  }, []);

  const handleDragMove = useCallback((e: React.MouseEvent | React.TouchEvent, section: 'classroom' | 'connect') => {
    e.preventDefault();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    
    if (section === 'classroom' && classroomDrag.isDragging) {
      const offset = clientX - classroomDrag.startX;
      const hasMoved = Math.abs(offset) > 5; // 5px 이상 움직이면 드래그로 간주
      
      setClassroomDrag(prev => ({
        ...prev,
        currentX: clientX,
        offset: offset,
        hasMoved: hasMoved || prev.hasMoved
      }));
    } else if (section === 'connect' && connectDrag.isDragging) {
      const offset = clientX - connectDrag.startX;
      const hasMoved = Math.abs(offset) > 5; // 5px 이상 움직이면 드래그로 간주
      
      setConnectDrag(prev => ({
        ...prev,
        currentX: clientX,
        offset: offset,
        hasMoved: hasMoved || prev.hasMoved
      }));
    }
  }, [classroomDrag.isDragging, classroomDrag.startX, connectDrag.isDragging, connectDrag.startX]);

  const handleDragEnd = useCallback((section: 'classroom' | 'connect') => {
    if (section === 'classroom' && classroomDrag.isDragging) {
      const dragDistance = classroomDrag.currentX - classroomDrag.startX;
      const threshold = 100;
      
      if (dragDistance > threshold && classroomPage > 0) {
        const newPage = classroomPage - 1;
        setClassroomPage(newPage);
        loadLecturePageIfNeeded(newPage);
      } else if (dragDistance < -threshold && classroomPage < lectureTotalPages - 1) {
        const newPage = classroomPage + 1;
        setClassroomPage(newPage);
        loadLecturePageIfNeeded(newPage);
      }
      
      const shouldPreventClick = classroomDrag.hasMoved;
      
      setClassroomDrag({
        isDragging: false,
        startX: 0,
        currentX: 0,
        offset: 0,
        hasMoved: false,
        preventClick: shouldPreventClick
      });
      
      // 드래그 후 200ms 동안 클릭 차단
      if (shouldPreventClick) {
        setTimeout(() => {
          setClassroomDrag(prev => ({ ...prev, preventClick: false }));
        }, 200);
      }
    } else if (section === 'connect' && connectDrag.isDragging) {
      const dragDistance = connectDrag.currentX - connectDrag.startX;
      const threshold = 100;
      
      if (dragDistance > threshold && connectPage > 0) {
        const newPage = connectPage - 1;
        setConnectPage(newPage);
        loadLecturerPageIfNeeded(newPage);
      } else if (dragDistance < -threshold && connectPage < lecturerTotalPages - 1) {
        const newPage = connectPage + 1;
        setConnectPage(newPage);
        loadLecturerPageIfNeeded(newPage);
      }
      
      const shouldPreventClick = connectDrag.hasMoved;
      
      setConnectDrag({
        isDragging: false,
        startX: 0,
        currentX: 0,
        offset: 0,
        hasMoved: false,
        preventClick: shouldPreventClick
      });
      
      // 드래그 후 200ms 동안 클릭 차단
      if (shouldPreventClick) {
        setTimeout(() => {
          setConnectDrag(prev => ({ ...prev, preventClick: false }));
        }, 200);
      }
    }
  }, [classroomDrag, connectDrag, classroomPage, connectPage, lectureTotalPages, lecturerTotalPages, loadLecturePageIfNeeded, loadLecturerPageIfNeeded]);

  // 컴포넌트 마운트 시 초기 데이터 로드
  useEffect(() => {
    loadCategories();
    loadAllLecturers(); // 처음 3페이지만 로드
    loadAllLectures();  // 처음 3페이지만 로드
  }, []);

  // 카테고리 카드 클릭 핸들러
  const handleCategoryClick = (routeName: string) => {
    navigate(`/class/category?category=${routeName}`);
  };

  // 강연자 카드 클릭 핸들러 (드래그와 구분)
  const handleLecturerCardClick = (lecturerId: number, e: React.MouseEvent) => {
    // 드래그가 발생했거나 클릭이 차단된 상태라면 클릭 이벤트 무시
    if (connectDrag.hasMoved || connectDrag.preventClick || connectDrag.isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    console.log('강연자 클릭:', lecturerId);
    navigate(`/coffee-chat/${lecturerId}`);
  };

  // 강의 카드 클릭 핸들러 (드래그와 구분)
  const handleLectureCardClick = (lectureId: number, e: React.MouseEvent) => {
    // 드래그가 발생했거나 클릭이 차단된 상태라면 클릭 이벤트 무시
    if (classroomDrag.hasMoved || classroomDrag.preventClick || classroomDrag.isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    console.log('강의 클릭:', lectureId);
    navigate(`/class/lecture/${lectureId}`);
  };

  return (
    <Layout>
      <Container>
        {/* 배너 섹션 */}
        <BannerSection>
          <BannerContent>
            <BannerTitle>MEDICAL AI CAMPUS.</BannerTitle>
            <BannerSubtitle>미래 의료를 향한 새로운 도약, 의료 AI와 함께하세요.</BannerSubtitle>
          </BannerContent>
        </BannerSection>

        {/* 메인 콘텐츠 컨테이너 */}
        <MainContainer>
          {/* EXPLORE THE CAMPUS 섹션 */}
          <Section>
            <SectionHeader>
              <SectionTitleGroup>
                <SectionTitle>캠퍼스 강의실 둘러보기</SectionTitle>
                <SectionCaption>의료 AI 강의를 카테고리별로 자유롭게 탐색하며, 관심 있는 수업을 찾아보세요.</SectionCaption>
              </SectionTitleGroup>
            </SectionHeader>
            <CategoryGrid>
              {categoriesLoading ? (
                // 스켈레톤 UI - 6개 카드 (텍스트 부분 제거)
                Array.from({ length: 6 }).map((_, index) => (
                  <CategoryCard key={`skeleton-${index}`} style={{ pointerEvents: 'none' }}>
                    <CategoryCardImage>
                      <SkeletonBackground />
                    </CategoryCardImage>
                  </CategoryCard>
                ))
              ) : (
                categories.map((category) => (
                  <CategoryCard 
                    key={category.id} 
                    onClick={() => handleCategoryClick(category.route_name)}
                  >
                    <CategoryCardImage 
                      style={{
                        backgroundImage: `url('${getCategoryImageWithFallback(category, 400, 400)}')`
                      }} 
                    />
                    <CategoryCardOverlay>
                      <CategoryCardTitle>{String(category.name || '')}</CategoryCardTitle>
                    </CategoryCardOverlay>
                  </CategoryCard>
                ))
              )}
            </CategoryGrid>
          </Section>

          {/* VISIT THE CLASSROOM 섹션 */}
          <Section>
            <SectionHeader>
              <SectionTitleGroup>
                <SectionTitle>입문자를 위한 스타터 커리큘럼</SectionTitle>
                <SectionCaption>의료 AI의 핵심만 모은 입문 커리큘럼으로 빠르고 쉽게 기초를 잡아드립니다.</SectionCaption>
              </SectionTitleGroup>
              <SectionTrailing>
                <PaginationButtons>
                  <PaginationContainer>
                    <PaginationContent>
                      <PaginationBtn 
                        onClick={() => handleClassroomPageChange('prev')}
                        disabled={classroomPage === 0}
                      >
                        <PaginationIcon isActive={classroomPage > 0}>
                          <img src="/images/left.svg" alt="Previous" />
                        </PaginationIcon>
                      </PaginationBtn>
                      <PaginationBtn 
                        onClick={() => handleClassroomPageChange('next')}
                        disabled={classroomPage >= lectureTotalPages - 1}
                      >
                        <PaginationIcon isRight isActive={classroomPage < lectureTotalPages - 1}>
                          <img src="/images/left.svg" alt="Next" />
                        </PaginationIcon>
                      </PaginationBtn>
                    </PaginationContent>
                  </PaginationContainer>
                </PaginationButtons>
              </SectionTrailing>
            </SectionHeader>
            <SlideContainer
              ref={classroomRef}
              onMouseDown={(e) => handleDragStart(e, 'classroom')}
              onMouseMove={(e) => handleDragMove(e, 'classroom')}
              onTouchStart={(e) => handleDragStart(e, 'classroom')}
              onTouchMove={(e) => handleDragMove(e, 'classroom')}
              onMouseUp={() => handleDragEnd('classroom')}
              onTouchEnd={() => handleDragEnd('classroom')}
              onMouseLeave={() => handleDragEnd('classroom')}
              style={{ 
                pointerEvents: classroomDrag.isDragging && classroomDrag.hasMoved ? 'none' : 'auto',
                userSelect: 'none'
              }}
            >
              {lectureLoading ? (
                // 스켈레톤 UI
                <LectureGrid>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <LectureCard 
                      key={`skeleton-${index}`}
                      style={{ pointerEvents: 'none' }}
                    >
                      <LectureCardImage>
                        <SkeletonBackground />
                        <LectureCardOverlay>
                          <LectureCardContent>
                            <SkeletonTitle />
                            <SkeletonSubtitle />
                          </LectureCardContent>
                        </LectureCardOverlay>
                      </LectureCardImage>
                    </LectureCard>
                  ))}
                </LectureGrid>
              ) : (
                <SlideWrapper style={{ 
                  transform: `translateX(calc(-${classroomPage * (100/lectureTotalPages)}% + ${classroomDrag.isDragging ? classroomDrag.offset : 0}px))`,
                  transition: classroomDrag.isDragging ? 'none' : 'transform 0.6s ease',
                  width: `${lectureTotalPages * 100}%`
                }}>
                  {Array.from({ length: lectureTotalPages }).map((_, pageIndex) => (
                    <SlidePage key={pageIndex} style={{ width: `${100/lectureTotalPages}%` }}>
                      <LectureGrid>
                        {allLectures[pageIndex] ? (
                          allLectures[pageIndex].map((lecture) => (
                            <LectureCard 
                              key={lecture.id}
                              onClick={(e) => handleLectureCardClick(lecture.id, e)}
                            >
                              <LectureCardImage>
                                <LectureCardBg style={{backgroundImage: lecture.thumbnail_url ? `url('${String(lecture.thumbnail_url)}')` : 'none'}} />
                                <LectureCardOverlay>
                                  <LectureCardContent>
                                    <LectureCardTitle>{String(lecture.title || '')}</LectureCardTitle>
                                    <LectureCardSubtitle>{String(lecture.caption || '')}</LectureCardSubtitle>
                                  </LectureCardContent>
                                </LectureCardOverlay>
                              </LectureCardImage>
                            </LectureCard>
                          ))
                        ) : (
                          // 로딩 중이거나 데이터가 없을 때 스켈레톤 표시
                          Array.from({ length: 3 }).map((_, index) => (
                            <LectureCard key={`skeleton-${pageIndex}-${index}`} style={{ pointerEvents: 'none' }}>
                              <LectureCardImage>
                                <SkeletonBackground />
                                <LectureCardOverlay>
                                  <LectureCardContent>
                                    <SkeletonTitle />
                                    <SkeletonSubtitle />
                                  </LectureCardContent>
                                </LectureCardOverlay>
                              </LectureCardImage>
                            </LectureCard>
                          ))
                        )}
                      </LectureGrid>
                    </SlidePage>
                  ))}
                </SlideWrapper>
              )}
            </SlideContainer>
          </Section>

          {/* CONNECT TO THE WORLD 섹션 */}
          <Section>
            <SectionHeader>
              <SectionTitleGroup>
                <SectionTitle>멘토링 라운지</SectionTitle>
                <SectionCaption>전문가와 편안하게 이야기를 나누며 실전 노하우와 인사이트를 얻을 수 있는 공간입니다.</SectionCaption>
              </SectionTitleGroup>
              <SectionTrailing>
                <PaginationButtons>
                  <PaginationContainer>
                    <PaginationContent>
                      <PaginationBtn 
                        onClick={() => handleConnectPageChange('prev')}
                        disabled={connectPage === 0}
                      >
                        <PaginationIcon isActive={connectPage > 0}>
                          <img src="/images/left.svg" alt="Previous" />
                        </PaginationIcon>
                      </PaginationBtn>
                      <PaginationBtn 
                        onClick={() => handleConnectPageChange('next')}
                        disabled={connectPage >= lecturerTotalPages - 1}
                      >
                        <PaginationIcon isRight isActive={connectPage < lecturerTotalPages - 1}>
                          <img src="/images/left.svg" alt="Next" />
                        </PaginationIcon>
                      </PaginationBtn>
                    </PaginationContent>
                  </PaginationContainer>
                </PaginationButtons>
              </SectionTrailing>
            </SectionHeader>
            <SlideContainer
              ref={connectRef}
              onMouseDown={(e) => handleDragStart(e, 'connect')}
              onMouseMove={(e) => handleDragMove(e, 'connect')}
              onTouchStart={(e) => handleDragStart(e, 'connect')}
              onTouchMove={(e) => handleDragMove(e, 'connect')}
              onMouseUp={() => handleDragEnd('connect')}
              onTouchEnd={() => handleDragEnd('connect')}
              onMouseLeave={() => handleDragEnd('connect')}
              style={{ 
                pointerEvents: connectDrag.isDragging && connectDrag.hasMoved ? 'none' : 'auto',
                userSelect: 'none'
              }}
            >
              {lecturerLoading ? (
                // 스켈레톤 UI
                <CoffeeChatGrid>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <CoffeeChatCard 
                      key={`skeleton-${index}`}
                      style={{ pointerEvents: 'none' }}
                    >
                      <CoffeeChatCardImage>
                        <SkeletonBackground />
                        <CoffeeChatCardOverlay>
                          <CoffeeChatCardContent>
                            <SkeletonTitle />
                            <SkeletonSubtitle />
                          </CoffeeChatCardContent>
                        </CoffeeChatCardOverlay>
                      </CoffeeChatCardImage>
                    </CoffeeChatCard>
                  ))}
                </CoffeeChatGrid>
              ) : (
                <SlideWrapper style={{ 
                  transform: `translateX(calc(-${connectPage * (100/lecturerTotalPages)}% + ${connectDrag.isDragging ? connectDrag.offset : 0}px))`,
                  transition: connectDrag.isDragging ? 'none' : 'transform 0.6s ease',
                  width: `${lecturerTotalPages * 100}%`
                }}>
                  {Array.from({ length: lecturerTotalPages }).map((_, pageIndex) => (
                    <SlidePage key={pageIndex} style={{ width: `${100/lecturerTotalPages}%` }}>
                      <CoffeeChatGrid>
                        {allLecturers[pageIndex] ? (
                          allLecturers[pageIndex].map((lecturer) => (
                            <CoffeeChatCard 
                              key={lecturer.id}
                              onClick={(e) => handleLecturerCardClick(lecturer.id, e)}
                            >
                              <CoffeeChatCardImage>
                                {!imageLoadStates[lecturer.id] && <SkeletonBackground />}
                                
                                <CoffeeChatCardBg 
                                  style={{
                                    backgroundImage: `url('${getLecturerImageWithFallback(lecturer.image_url, lecturer.id)}')`,
                                    opacity: imageLoadStates[lecturer.id] ? 1 : 0
                                  }} 
                                />
                                
                                <img
                                  src={getLecturerImageWithFallback(lecturer.image_url, lecturer.id)}
                                  alt=""
                                  style={{ display: 'none' }}
                                  onLoad={() => handleImageLoad(lecturer.id)}
                                  onError={() => handleImageLoad(lecturer.id)}
                                />
                                
                                <CoffeeChatCardOverlay>
                                  <CoffeeChatCardContent>
                                    {imageLoadStates[lecturer.id] ? (
                                      <>
                                        <CoffeeChatCardTitle>{String(lecturer.name || '')}</CoffeeChatCardTitle>
                                        <CoffeeChatCardSubtitle>{String(lecturer.introduction || '')}</CoffeeChatCardSubtitle>
                                      </>
                                    ) : (
                                      <>
                                        <SkeletonTitle />
                                        <SkeletonSubtitle />
                                      </>
                                    )}
                                  </CoffeeChatCardContent>
                                </CoffeeChatCardOverlay>
                              </CoffeeChatCardImage>
                            </CoffeeChatCard>
                          ))
                        ) : (
                          // 로딩 중이거나 데이터가 없을 때 스켈레톤 표시
                          Array.from({ length: 4 }).map((_, index) => (
                            <CoffeeChatCard key={`skeleton-${pageIndex}-${index}`} style={{ pointerEvents: 'none' }}>
                              <CoffeeChatCardImage>
                                <SkeletonBackground />
                                <CoffeeChatCardOverlay>
                                  <CoffeeChatCardContent>
                                    <SkeletonTitle />
                                    <SkeletonSubtitle />
                                  </CoffeeChatCardContent>
                                </CoffeeChatCardOverlay>
                              </CoffeeChatCardImage>
                            </CoffeeChatCard>
                          ))
                        )}
                      </CoffeeChatGrid>
                    </SlidePage>
                  ))}
                </SlideWrapper>
              )}
            </SlideContainer>
          </Section>
        </MainContainer>
      </Container>
    </Layout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  min-height: 100vh;
`;

/* 배너 섹션 */
const BannerSection = styled.section`
  height: 480px;
  background: url('/images/banner-main.png') center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  width: 100vw;
  margin-left: calc(50% - 50vw);
  
  @media (max-width: 768px) {
    height: 400px;
  }
`;

const BannerContent = styled.div`
  max-width: ${({ theme }) => theme.layout.containerWidth};
  width: 100%;
  padding: 0 ${({ theme }) => theme.layout.containerPadding};
  color: var(--Static-White, white);
  z-index: 2;
  
  @media (max-width: 1024px) {
    padding: 0 ${({ theme }) => theme.layout.containerPaddingTablet};
  }
  
  @media (max-width: 768px) {
    padding: 0 ${({ theme }) => theme.layout.containerPaddingMobile};
  }
`;

const BannerTitle = styled.h1`
  color: var(--Static-White, white);
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 56px;
  font-weight: 700;
  line-height: 72.02px;
  word-wrap: break-word;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 41.6px;
  }
`;

const BannerSubtitle = styled.h2`
  color: var(--Static-White, white);
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 22px;
  font-weight: 400;
  line-height: 30.01px;
  word-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 24.6px;
  }
`;

/* 메인 컨테이너 */
const MainContainer = styled.div`
  max-width: ${({ theme }) => theme.layout.containerWidth};
  margin: 0 auto;
  padding: 64px ${({ theme }) => theme.layout.containerPadding} 128px;
  display: flex;
  flex-direction: column;
  gap: 64px;
  
  @media (max-width: 1024px) {
    padding: 64px ${({ theme }) => theme.layout.containerPaddingTablet} 128px;
  }
  
  @media (max-width: 768px) {
    padding: 40px ${({ theme }) => theme.layout.containerPaddingMobile} 100px;
    gap: 48px;
  }
`;

/* 섹션 공통 스타일 */
const Section = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const SectionHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const SectionTitleGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled.h2`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.358;
  letter-spacing: -2.36%;
  color: #000000;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const SectionCaption = styled.p`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.445;
  letter-spacing: -0.02%;
  color: rgba(55, 56, 60, 0.61);
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SectionTrailing = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  height: 38px;
  
  @media (max-width: 768px) {
    align-self: flex-end;
  }
`;

/* 페이지네이션 버튼 */
const PaginationButtons = styled.div`
  height: 38px;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
  display: inline-flex;
`;

const PaginationContainer = styled.div`
  position: relative;
  border-radius: 10px;
  outline: 1px rgba(112, 115, 124, 0.16) solid;
  outline-offset: -1px;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const PaginationContent = styled.div`
  overflow: hidden;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const PaginationBtn = styled.button<{ disabled?: boolean }>`
  padding: 7px;
  justify-content: center;
  align-items: center;
  display: flex;
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  
  &:disabled {
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(23, 23, 25, 0.04);
      border-radius: 6px;
    }
  }
  
  &:first-child {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }
  
  &:last-child {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`;

const PaginationIcon = styled.div<{ isRight?: boolean; isActive?: boolean }>`
  height: 18px;
  width: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  transform: ${props => props.isRight ? 'rotate(180deg)' : 'none'};
  
  img {
    width: 18px;
    height: 18px;
    filter: ${props => props.isActive ? 'brightness(0) saturate(100%) invert(0%)' : 'opacity(0.35)'};
    transition: filter 0.2s ease;
  }
`;

/* 슬라이드 애니메이션 컴포넌트 */
const SlideContainer = styled.div`
  width: 100%;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  
  &:active {
    cursor: grabbing;
  }
  
  * {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  width: 300%; /* 3페이지 * 100% */
  transition: transform 0.6s ease;
  will-change: transform;
`;

const SlidePage = styled.div`
  width: calc(100% / 3); /* 전체 너비의 1/3 */
  flex-shrink: 0;
`;

/* 카테고리 카드 그리드 (6개, 1:1 비율) */
const CategoryGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const CategoryCard = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid rgba(112, 115, 124, 0.16);
`;

const CategoryCardImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  background-color: #f5f5f5; /* 기본 배경색 */
  transition: transform 0.3s ease; /* 기본 상태에 transition 적용 */
  
  ${CategoryCard}:hover & {
    transform: scale(1.05);
  }
`;

const CategoryCardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const CategoryCardTitle = styled.h3`
  color: white;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

/* 강의 카드 그리드 (3개, 3:2 비율) */
const LectureGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const LectureCard = styled.div`
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid rgba(112, 115, 124, 0.08);
`;

const LectureCardImage = styled.div`
  width: 100%;
  aspect-ratio: 3/2;
  position: relative;
  overflow: hidden;
`;

const LectureCardBg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  transition: transform 0.3s ease; /* 기본 상태에 transition 적용 */
  
  ${LectureCard}:hover & {
    transform: scale(1.05);
  }
`;

const LectureCardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 72px);
  display: flex;
  align-items: flex-end;
  padding: 24px;
`;

const LectureCardContent = styled.div`
  color: white;
`;

const LectureCardTitle = styled.h3`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: -0.012em;
  margin-bottom: 4px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const LectureCardSubtitle = styled.p`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.429;
  letter-spacing: 0.0145em;
  opacity: 0.9;
`;

/* 커피챗 카드 그리드 (4개, 1:1 비율) */
const CoffeeChatGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const CoffeeChatCard = styled.div`
  position: relative;
  aspect-ratio: 1;
  min-height: 200px; /* 최소 높이 설정 */
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid rgba(112, 115, 124, 0.08);
`;

const CoffeeChatCardImage = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const CoffeeChatCardBg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  background-color: #f5f5f5; /* 이미지 로드 전 배경색 */
  transition: transform 0.3s ease; /* 기본 상태에 transition 적용 */
  
  ${CoffeeChatCard}:hover & {
    transform: scale(1.05);
  }
`;

const CoffeeChatCardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 72px);
  display: flex;
  align-items: flex-end;
  padding: 24px;
`;

const CoffeeChatCardContent = styled.div`
  color: white;
`;

const CoffeeChatCardTitle = styled.h3`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: -0.012em;
  margin-bottom: 4px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const CoffeeChatCardSubtitle = styled.p`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.429;
  letter-spacing: 0.0145em;
  opacity: 0.9;
`;

/* 스켈레톤 UI 컴포넌트들 */
const SkeletonBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonTitle = styled.div`
  width: 80%;
  height: 20px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  margin-bottom: 8px;
  animation: pulse 1.5s ease-in-out infinite alternate;
  
  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    100% {
      opacity: 0.8;
    }
  }
`;

const SkeletonSubtitle = styled.div`
  width: 60%;
  height: 14px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite alternate;
  animation-delay: 0.2s;
  
  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    100% {
      opacity: 0.8;
    }
  }
`;

export default Home;